import React, { useEffect } from 'react'
import { useSession } from 'src/sdk/session'
import { useInView } from 'react-intersection-observer'
import { usePromoView } from 'src/sdk/analytics/hooks/usePromoViewEvent'
import { usePromoClick } from 'src/sdk/analytics/hooks/usePromoClickEvent'
import { useEventPromoView } from 'src/hooks/usePromoView'
import { useErrorComponentEvent } from 'src/sdk/analytics/hooks/useErrorComponentEvent'
import { EventPromoClick } from 'src/components/common/EventPromoClick'
import { getCookie, setCookie } from 'src/utils/cookie'
import { Link } from 'gatsby'
import { Helmet } from 'react-helmet'
import useWindowDimensions from 'src/hooks/useWindowDimensions'
import errorFunction from 'src/utils/error/errorFunction'
import PreloadedImage from 'src/components/common/PreloadedImage/PreloadedImage'
import { sourceMedia } from 'src/utils/returnSourceMedia'
import { JsonLd } from 'gatsby-plugin-next-seo'

import { formatAlt } from '../../../utils/formatAlt'
import './fullwidthStaticBanner.scss'

interface FullwidthStaticBannerSources {
  media: string
  srcSet: string
  width: string
  height: string
}

function FullwidthStaticBanner(
  props: Readonly<{
    promotion: boolean
    campaignEco?: string
    promotionId?: string
    promotionCategory?: string
    promotionName?: string
    promotionPosition?: string
    sources: FullwidthStaticBannerSources[]
    alt: string
    href: string
  }>
) {
  const { person } = useSession()
  const { sendPromoViewEvent } = usePromoView()
  const { sendPromoClickEvent } = usePromoClick()
  const { width } = useWindowDimensions()
  const { sendErrorComponentEvent } = useErrorComponentEvent(
    'FullWidthStaticBanner'
  )

  const uuidToUse = getCookie('uuid')

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.75,
  })

  const { creativeFormat, idFormat, nameFormat, positionFormat } = formatAlt(
    props?.alt
  )

  const { sendPromoView } = useEventPromoView(uuidToUse, [
    {
      id: idFormat,
      name: nameFormat,
      creative: creativeFormat,
      position: positionFormat,
    },
  ])

  const {
    promotion,
    campaignEco,
    promotionId,
    promotionCategory,
    promotionName,
    promotionPosition,
    alt,
  } = props

  const isMobile = width < 640

  const sourceMobile = sourceMedia(
    props?.sources,
    '(max-width: 48em)'
  ) as FullwidthStaticBannerSources[]

  const sourceDesktop = sourceMedia(
    props?.sources,
    '(min-width: 48em)'
  ) as FullwidthStaticBannerSources[]

  const fullWidthBannerClass = `fullWidthBannerWrapper-${sourceDesktop?.[0]?.width}-${sourceDesktop?.[0]?.height}-${sourceMobile?.[0]?.width}-${sourceMobile?.[0]?.height}`

  const mobileAspectRatio = `${sourceMobile?.[0]?.width ?? 1} / ${
    sourceMobile?.[0]?.height ?? 1
  }`

  const desktopAspectRatio = `${sourceDesktop?.[0]?.width ?? 1} / ${
    sourceDesktop?.[0]?.height ?? 1
  }`

  const desktopSrc = props?.sources.filter(
    (source: Sources) => source?.media === '(min-width: 48em)'
  )[0]?.srcSet

  const mobileSrc = props?.sources.filter(
    (source: Sources) => source?.media === '(max-width: 48em)'
  )[0]?.srcSet

  useEffect(() => {
    if (promotion && inView) {
      sendPromoViewEvent({
        campaign_eco: campaignEco,
        promotion_id: promotionId as string,
        promotion_category: promotionCategory as string,
        promotion_name: promotionName as string,
        promotion_creative: alt,
        promotion_position: promotionPosition as string,
        page_location: window.location.href,
        user_logged: !!person?.id,
        user_id: person?.id ?? null,
        uuid: uuidToUse,
      })

      if (campaignEco) {
        setCookie('campanhaEco', campaignEco, 7, '.vivara.com.br')
      }
    }
  }, [inView])

  useEffect(() => {
    if (
      !props?.alt ||
      props?.sources.length === 0 ||
      !props?.sources?.[0]?.srcSet ||
      !props?.sources?.[0]?.media ||
      !props?.sources?.[0]?.height ||
      !props?.sources?.[0]?.width
    ) {
      errorFunction(props, 'FullWidthStaticBanner', sendErrorComponentEvent)
    }
  }, [props, sendErrorComponentEvent, sendPromoView])

  useEffect(() => {
    if (props?.alt !== '') {
      sendPromoView()
    }
  }, [props?.alt])

  if (
    !props?.alt ||
    props?.sources.length === 0 ||
    !props?.sources?.[0]?.srcSet ||
    !props?.sources?.[0]?.media ||
    !props?.sources?.[0]?.height ||
    !props?.sources?.[0]?.width
  ) {
    return <></>
  }

  return (
    <>
      <Helmet>
        <style>
          {`.${fullWidthBannerClass}{
              aspect-ratio: ${mobileAspectRatio};
            }
            @media screen and (min-width: 640px) {
              .${fullWidthBannerClass}{
                aspect-ratio: ${desktopAspectRatio};
              }
            }`}
        </style>
      </Helmet>

      <div
        ref={ref}
        className={`fullWidthBannerWrapper ${fullWidthBannerClass}`}
      >
        <Link
          to={props?.href}
          className="fullwidthBanner-container"
          onClick={() => {
            EventPromoClick(
              props?.alt,
              isMobile ? sourceMobile?.[0]?.srcSet : sourceDesktop?.[0]?.srcSet
            )

            if (promotion) {
              sendPromoClickEvent({
                campaign_eco: campaignEco,
                promotion_id: promotionId as string,
                promotion_category: promotionCategory as string,
                promotion_name: promotionName as string,
                promotion_creative: props.alt,
                position: promotionPosition as string,
                user_logged: !!person?.id,
                user_id: person?.id ?? null,
                uuid: uuidToUse,
              })

              if (campaignEco) {
                setCookie('campanhaEco', campaignEco, 7, '.vivara.com.br')
              }
            }
          }}
        >
          <PreloadedImage
            widthImage="100%"
            mobileSrc={mobileSrc}
            desktopSrc={desktopSrc}
            isMobile={isMobile}
            mobileAspectRatio={mobileAspectRatio}
            desktopAspectRatio={desktopAspectRatio}
            alt={props?.alt}
          />
        </Link>
      </div>

      <JsonLd
        json={{
          '@context': 'https://schema.org',
          '@type': 'ImageObject',
          name: 'Imagem do FullWidthStaticBanner',
          caption: props?.alt,
          contentUrl: isMobile ? mobileSrc : desktopSrc,
          url: isMobile ? mobileSrc : desktopSrc,
          description: props?.alt,
          width: isMobile
            ? sourceMobile?.[0]?.width
            : sourceDesktop?.[0]?.width,
          height: isMobile
            ? sourceMobile?.[0]?.height
            : sourceDesktop?.[0]?.height,
        }}
      />
    </>
  )
}

export default FullwidthStaticBanner
